import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  merge,
  startWith,
  Subject,
  switchMap,
  tap
} from 'rxjs';
import { PromotionsService } from 'core/services/promotions.service';
import { IActiveCampaign } from 'core/types/campaign.interface';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';

@Component({
  selector: 'app-referral-active-missions',
  imports: [RouterLink, TranslatePipe, KorviuLoaderComponent],
  templateUrl: './referral-active-missions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.hidden]': '!isLoading() && !loadError() && missions().length === 0' }
})
export class ReferralActiveMissionsComponent implements OnInit {
  readonly #promotionsService = inject(PromotionsService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #translateService = inject(TranslateService);
  readonly #reloadMissions$ = new Subject<void>();

  readonly campaigns = signal<IActiveCampaign[]>([]);
  readonly total = signal(0);
  readonly isLoading = signal(true);
  readonly loadError = signal(false);
  readonly missions = computed(() =>
    this.campaigns().map(campaign => ({
      ...campaign,
      progress:
        campaign.objective_progress && campaign.objective_progress.target > 0
          ? Math.min(100, Math.max(0, (campaign.objective_progress.current / campaign.objective_progress.target) * 100))
          : 0
    }))
  );

  ngOnInit(): void {
    merge(
      this.#reloadMissions$,
      this.#translateService.onLangChange.pipe(
        map(event => event.lang),
        startWith(this.#translateService.currentLang || this.#translateService.defaultLang || 'es'),
        distinctUntilChanged()
      )
    )
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.loadError.set(false);

          return this.#promotionsService.getActiveCampaigns({ page: 1, pageSize: 2 }).pipe(
            tap(response => {
              this.campaigns.set(response.results);
              this.total.set(response.count);
            }),
            catchError(() => {
              this.campaigns.set([]);
              this.total.set(0);
              this.loadError.set(true);
              return EMPTY;
            }),
            finalize(() => this.isLoading.set(false))
          );
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  loadMissions(): void {
    this.#reloadMissions$.next();
  }
}
